<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import MarkdownIt from 'markdown-it';
import hljs from 'highlight.js/lib/common';
import 'highlight.js/styles/default.css';
import { useStreamMarkdown } from 'shared/composables/useStreamMarkdown';
import { parseTaggedContent } from 'shared/utils/copilotTagParser';
import {
  parseAttachedMentions,
  stripAttachedContext,
  mentionToPanel,
  mentionChipIcon,
  hrefToPanel,
  entityTypeFromHref,
} from 'shared/helpers/copilotMentionContext';
import { getToolStatusLabel } from 'shared/utils/copilotToolLabels';
import { resolveMessageFeatherVariant } from 'shared/composables/copilot/useFeatherLoaderMotion';
import CopilotPricingItemCard from './CopilotPricingItemCard.vue';
import CopilotFeatherLoader from './CopilotFeatherLoader.vue';
import CopilotReasoning from './CopilotReasoning.vue';
import CopilotRotatingText from './CopilotRotatingText.vue';
const props = defineProps({
  message: { type: Object, required: true },
  isStreaming: { type: Boolean, default: false },
  isPausedForToolConsent: { type: Boolean, default: false },
  // awaiting | approved | rejected — drives tool-log label through consent.
  toolConsentPhase: { type: String, default: null },
  toolStatusById: { type: Object, default: () => ({}) },
  isLast: { type: Boolean, default: false },
  isFirst: { type: Boolean, default: false },
  tightToPrevious: { type: Boolean, default: false },
  pendingLabel: { type: String, default: '' },
  mergedTurnReasoning: { type: String, default: null },
  hideReasoning: { type: Boolean, default: false },
  reasoningStreaming: { type: Boolean, default: false },
  suppressReasoningOnlyBubble: { type: Boolean, default: false },
});

const emit = defineEmits(['retry', 'openPanel']);

const router = useRouter();
const { t } = useI18n();

const PROSE_CLASSES =
  'text-[15px] leading-7 break-words prose prose-sm dark:prose-invert max-w-none [&_ul]:my-3 [&_ol]:my-3 [&_li]:my-1 [&_table]:my-4 [&_p:first-child]:mt-0 [&_p:last-child]:mb-0 [&_pre]:!my-0 [&_code]:text-[13px] [&_a.copilot-entity-link]:text-n-brand [&_a.copilot-entity-link]:font-medium';

function escapeHtml(str) {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function hasNonEmptyText(value) {
  return typeof value === 'string' && value.trim().length > 0;
}

function renderHighlightedCode(str, lang) {
  const language = lang && hljs.getLanguage(lang) ? lang : null;
  const highlighted = language
    ? hljs.highlight(str, { language }).value
    : escapeHtml(str);
  const langLabel = language || 'text';
  const copyLabel = escapeHtml(t('COPILOT.COPY_ANSWER'));

  return `<div data-copilot-code-block class="group/code relative my-4 rounded-xl overflow-hidden ring-1 ring-n-slate-6 bg-n-slate-12 dark:bg-n-solid-2"><div class="flex items-center justify-between px-3 py-2 bg-n-slate-11 dark:bg-n-solid-3 border-b border-n-slate-9 dark:border-n-weak"><span class="text-xs font-medium text-n-slate-4 dark:text-n-slate-9 uppercase tracking-wide">${langLabel}</span><button type="button" class="copilot-code-copy opacity-0 group-hover/code:opacity-100 flex items-center gap-1 px-2 py-0.5 rounded text-xs text-n-slate-4 dark:text-n-slate-9 hover:text-white dark:hover:text-n-slate-12 hover:bg-n-slate-9 dark:hover:bg-n-solid-4 transition-all">${copyLabel}</button></div><pre class="!m-0 !p-4 overflow-x-auto text-sm !bg-transparent"><code class="hljs language-${langLabel} !bg-transparent">${highlighted}</code></pre></div>`;
}

const md = new MarkdownIt({
  html: false,
  linkify: true,
  breaks: true,
  highlight: (str, lang) => renderHighlightedCode(str, lang),
});

const defaultLinkOpen =
  md.renderer.rules.link_open ||
  ((tokens, idx, options, _env, self) => self.renderToken(tokens, idx, options));

md.renderer.rules.link_open = (tokens, idx, options, env, self) => {
  const token = tokens[idx];
  const href = token.attrGet('href');
  const entityType = entityTypeFromHref(href);
  if (!entityType) {
    return defaultLinkOpen(tokens, idx, options, env, self);
  }

  token.attrJoin(
    'class',
    'copilot-entity-link inline max-w-full no-underline hover:underline underline-offset-2'
  );
  token.attrSet('data-entity-type', entityType);

  const open = defaultLinkOpen(tokens, idx, options, env, self);
  const icon = mentionChipIcon(entityType);
  return `${open}<span class="mr-1 inline-block size-3.5 align-[-0.125em] ${icon}" aria-hidden="true"></span>`;
};

const isHuman = computed(() => {
  const type = props.message.type || props.message._getType?.();
  return type === 'human';
});

const isAI = computed(() => {
  const type = props.message.type || props.message._getType?.();
  return type === 'ai';
});

const isSystem = computed(() => {
  const type = props.message.type || props.message._getType?.();
  return type === 'system';
});

const isTool = computed(() => {
  const type = props.message.type || props.message._getType?.();
  return type === 'tool';
});

// Pattern for file content extracted by the backend (preprocess_content in base.py).
// After reload from LangGraph state, file blocks become text blocks like:
//   "[Attached file: name.pdf (application/pdf)]\n\n--- Page 1 ---\n..."
const ATTACHED_FILE_RE = /\[Attached file: (.+?) \((.+?)\)\]/;

function stripFileContent(text) {
  if (!text) return text;
  if (!ATTACHED_FILE_RE.test(text)) return text;
  // Remove "[Attached file: ...]\n\n{extracted content}" and everything after it.
  // Keep any user text that precedes the attachment marker.
  const idx = text.search(ATTACHED_FILE_RE);
  return text.slice(0, idx).trim();
}

function parseAttachedFiles(text) {
  return text
    .split('\n')
    .map(line => line.match(ATTACHED_FILE_RE))
    .filter(Boolean)
    .map(match => ({ name: match[1], mimeType: match[2] }));
}

function getMessageText(raw) {
  if (typeof raw === 'string') return raw;
  if (Array.isArray(raw)) {
    return raw
      .filter(block => block.type === 'text')
      .map(block => block.text)
      .join('\n');
  }
  return '';
}

function stripDisplayContent(text) {
  return stripFileContent(stripAttachedContext(text));
}

const messageText = computed(() => getMessageText(props.message.content));

const attachedMentions = computed(() =>
  parseAttachedMentions(messageText.value)
);

function handleMentionOpen(mention) {
  const panel = mentionToPanel(mention);
  if (panel) emit('openPanel', panel);
}

const rawContent = computed(() => stripDisplayContent(messageText.value));

// Keep last known text across brief SDK content gaps when a stream finishes.
const lastStableRawContent = ref('');
const lastKnownResponse = ref('');
const lastKnownReasoning = ref('');

watch(
  rawContent,
  value => {
    if (hasNonEmptyText(value)) lastStableRawContent.value = value;
  },
  { immediate: true }
);

watch(
  () => props.message.id,
  (newId, oldId) => {
    // LangGraph assigns ids late — don't wipe cached content on first id assignment.
    if (oldId != null && newId != null && newId !== oldId) {
      // Pending shell promotes to real LangGraph id — keep streamed content.
      if (oldId === '__pending_ai__') return;
      lastStableRawContent.value = '';
      lastKnownResponse.value = '';
      lastKnownReasoning.value = '';
    }
  }
);

const contentForParsing = computed(
  () => rawContent.value || lastStableRawContent.value
);

// Extract file attachments from text blocks that were preprocessed by the backend
const extractedFiles = computed(() => {
  const raw = props.message.content;
  if (!Array.isArray(raw)) {
    if (typeof raw === 'string') {
      return parseAttachedFiles(raw);
    }
    return [];
  }
  return raw
    .filter(block => block.type === 'text' && ATTACHED_FILE_RE.test(block.text))
    .map(block => {
      const match = block.text.match(ATTACHED_FILE_RE);
      return { name: match[1], mimeType: match[2] };
    });
});

// ---------------------------------------------------------------------------
// Structured tag parsing — see shared/utils/copilotTagParser.js
// ---------------------------------------------------------------------------

const parsedSegments = computed(() =>
  parseTaggedContent(contentForParsing.value, true)
);

// Has the message any <response> or plain-text content?
const hasTextContent = computed(() =>
  parsedSegments.value.some(
    s => ['text', 'response'].includes(s.type) && s.content
  )
);

const hasAnswerContent = computed(() => {
  const segs = parsedSegments.value || [];
  if (
    segs.some(
      s =>
        ['text', 'response', 'action'].includes(s.type) &&
        hasNonEmptyText(s.content)
    )
  ) {
    return true;
  }
  if (segs.some(s => s.type === 'rate' && s.content)) return true;
  if (segs.length === 0 && hasNonEmptyText(contentForParsing.value))
    return true;
  if (hasNonEmptyText(lastKnownResponse.value)) return true;
  return false;
});

const imageBlocks = computed(() => {
  const raw = props.message.content;
  if (!Array.isArray(raw)) return [];
  return raw.filter(block => block.type === 'image');
});

const fileBlocks = computed(() => {
  const raw = props.message.content;
  if (!Array.isArray(raw)) return [];
  return raw.filter(block => block.type === 'file');
});

const hasThinking = computed(() => {
  if (!Array.isArray(props.message.content)) return false;
  return props.message.content.some(
    block => block.type === 'thinking' && hasNonEmptyText(block.thinking)
  );
});

const anthropicThinkingContent = computed(() => {
  if (!Array.isArray(props.message.content)) return '';
  return props.message.content
    .filter(
      block => block.type === 'thinking' && hasNonEmptyText(block.thinking)
    )
    .map(block => block.thinking)
    .join('\n');
});

const taggedThinkingContent = computed(() =>
  parsedSegments.value
    .filter(s => s.type === 'thinking' && hasNonEmptyText(s.content))
    .map(s => s.content)
    .join('\n\n')
);

const reasoningContent = computed(() => {
  if (hasNonEmptyText(anthropicThinkingContent.value)) {
    return anthropicThinkingContent.value;
  }
  return taggedThinkingContent.value;
});

watch(
  reasoningContent,
  value => {
    if (hasNonEmptyText(value)) lastKnownReasoning.value = value;
  },
  { immediate: true }
);

const displayReasoningContent = computed(() => {
  if (props.hideReasoning) return '';
  // Empty merged string must not wipe lastKnown during brief SDK content gaps.
  if (hasNonEmptyText(props.mergedTurnReasoning)) {
    return props.mergedTurnReasoning;
  }
  return reasoningContent.value || lastKnownReasoning.value;
});

const isThinkingInProgress = computed(() => {
  if (props.reasoningStreaming) return true;
  if (!props.isStreaming) return false;

  const segs = parsedSegments.value.filter(s => hasNonEmptyText(s.content));
  if (segs.length > 0) {
    if (segs[segs.length - 1].type === 'thinking') return true;
    if (segs.some(s => ['response', 'text'].includes(s.type))) return false;
  }

  return hasThinking.value && !hasNonEmptyText(streamingResponseContent.value);
});

const streamingResponseContent = computed(() =>
  parsedSegments.value
    .filter(
      s => ['response', 'text'].includes(s.type) && hasNonEmptyText(s.content)
    )
    .map(s => s.content)
    .join('\n\n')
);

// Only mount when there is real reasoning text — an empty "Thinking" shell
// during the wait would disappear the moment the stream ends.
const showReasoning = computed(
  () =>
    !props.hideReasoning &&
    isAI.value &&
    hasNonEmptyText(displayReasoningContent.value)
);

const activeResponseContent = computed(
  () => streamingResponseContent.value || lastKnownResponse.value
);

const displayResponseMarkdown = computed(() => {
  if (hasNonEmptyText(activeResponseContent.value)) {
    return activeResponseContent.value;
  }
  return parsedSegments.value
    .filter(
      s => ['response', 'text'].includes(s.type) && hasNonEmptyText(s.content)
    )
    .map(s => s.content)
    .join('\n\n');
});

const hasAiBodyContent = computed(
  () =>
    hasNonEmptyText(contentForParsing.value) ||
    hasThinking.value ||
    hasToolCalls.value ||
    hasNonEmptyText(lastKnownResponse.value) ||
    hasNonEmptyText(displayResponseMarkdown.value)
);

watch(streamingResponseContent, content => {
  if (hasNonEmptyText(content)) lastKnownResponse.value = content;
});

function render(text) {
  return md.render(text || '');
}

const { html: streamProseHtml } = useStreamMarkdown(displayResponseMarkdown, {
  render,
  isStreaming: () => props.isStreaming,
});

function handleContentClick(event) {
  const copyBtn = event.target.closest('.copilot-code-copy');
  if (copyBtn) {
    event.preventDefault();
    const block = copyBtn.closest('[data-copilot-code-block]');
    const code = block?.querySelector('code');
    if (code) {
      navigator.clipboard.writeText(code.textContent || '');
    }
    return;
  }

  const link = event.target.closest('a');
  if (!link) return;

  const href = link.getAttribute('href');
  if (!href) return;

  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

  const panel = hrefToPanel(href);
  if (panel) {
    event.preventDefault();
    const title = link.textContent?.trim();
    if (title && title !== panel.id) panel.title = title;
    emit('openPanel', panel);
    return;
  }

  if (href.startsWith('/app/accounts/')) {
    event.preventDefault();
    router.push(href);
  }
}

function hasToolOutput(call) {
  if (!call) return false;
  const payload = call.output ?? call.result;
  if (payload == null) return false;
  if (typeof payload === 'string') return payload.length > 0;
  if (Array.isArray(payload)) return payload.length > 0;
  if (typeof payload === 'object') {
    if (Array.isArray(payload.content)) return payload.content.length > 0;
    if (typeof payload.content === 'string') return payload.content.length > 0;
    return Object.keys(payload).length > 0;
  }
  return true;
}

function normalizeToolStatus(call) {
  const callId = String(call?.id || '');
  const mappedStatus = callId ? props.toolStatusById[callId] : null;
  const status = call?.status;

  if (
    ['error', 'failed', 'failure', 'cancelled', 'aborted'].includes(
      mappedStatus
    )
  )
    return 'error';
  if (
    [
      'finished',
      'complete',
      'completed',
      'done',
      'success',
      'succeeded',
    ].includes(mappedStatus)
  )
    return 'finished';
  if (['pending', 'queued'].includes(mappedStatus)) return 'pending';
  if (['running', 'in_progress', 'started'].includes(mappedStatus))
    return 'running';

  if (['error', 'failed', 'failure', 'cancelled', 'aborted'].includes(status))
    return 'error';
  if (
    [
      'finished',
      'complete',
      'completed',
      'done',
      'success',
      'succeeded',
    ].includes(status)
  )
    return 'finished';

  // If this message is no longer streaming, treat non-error tool calls as finished,
  // except while waiting for tool-consent approval.
  if (!props.isStreaming && !props.isPausedForToolConsent) return 'finished';

  if (
    ['running', 'pending', 'in_progress', 'started', 'queued'].includes(status)
  ) {
    if (call?.error) return 'error';
    if (hasToolOutput(call)) return 'finished';
  }

  return status || 'running';
}

const messageToolCalls = computed(() =>
  (props.message.tool_calls || []).map(call => ({
    ...call,
    status: normalizeToolStatus(call),
  }))
);
const hasToolCalls = computed(() => messageToolCalls.value.length > 0);
const hasRunningToolCalls = computed(() =>
  messageToolCalls.value.some(
    tc => tc.status === 'running' || tc.status === 'pending'
  )
);

const messageFeatherVariant = computed(() =>
  resolveMessageFeatherVariant({
    isStreaming: props.isStreaming,
    isThinkingInProgress: isThinkingInProgress.value,
    hasRunningToolCalls: hasRunningToolCalls.value,
    hasResponseContent: hasNonEmptyText(streamingResponseContent.value),
  })
);

const messageFeatherAnimated = computed(() => props.isStreaming);

const showMessageFeather = computed(() => isAI.value && props.isLast);

const messageFeatherAnchorRef = ref(null);
const messageRowRef = ref(null);

const expandedToolCallGroups = ref({});

const groupedMessageToolCalls = computed(() => {
  return messageToolCalls.value.reduce((groups, call) => {
    const prev = groups[groups.length - 1];
    const prevStatus = prev?.status ?? null;
    const callStatus = call?.status ?? null;
    if (prev && prevStatus === callStatus) {
      prev.calls.push(call);
      return groups;
    }

    groups.push({
      key: `${call.name}:${groups.length}`,
      name: call.name,
      status: callStatus,
      calls: [call],
    });
    return groups;
  }, []);
});

function toggleToolCallGroup(groupKey) {
  expandedToolCallGroups.value = {
    ...expandedToolCallGroups.value,
    [groupKey]: !expandedToolCallGroups.value[groupKey],
  };
}

function isToolCallGroupExpanded(groupKey) {
  return !!expandedToolCallGroups.value[groupKey];
}

function formatToolTitle(name) {
  const bare = name?.includes('.') ? name.split('.').pop() : name;
  const words = (bare || '').replace(/_/g, ' ').trim();
  return words.replace(/^\w/, c => c.toUpperCase());
}

function getToolDisplayName(name, status) {
  if (props.toolConsentPhase === 'awaiting' || props.isPausedForToolConsent) {
    return formatToolTitle(name);
  }
  if (props.toolConsentPhase === 'approved') {
    return t('COPILOT.TOOL_APPROVED', { tools: formatToolTitle(name) });
  }
  if (props.toolConsentPhase === 'rejected') {
    return t('COPILOT.TOOL_DENIED', { tools: formatToolTitle(name) });
  }
  return getToolStatusLabel(name, status);
}

function getToolStatusMeta(status) {
  if (props.toolConsentPhase === 'approved') {
    return {
      label: 'Approved',
      icon: 'i-lucide-check-circle-2',
      colorClass: 'text-n-green-11',
      badgeClass: 'text-n-green-11 bg-n-green-3',
    };
  }

  if (props.toolConsentPhase === 'rejected') {
    return {
      label: 'Denied',
      icon: 'i-lucide-x-circle',
      colorClass: 'text-n-ruby-11',
      badgeClass: 'text-n-ruby-11 bg-n-ruby-3',
    };
  }

  if (props.toolConsentPhase === 'awaiting' || props.isPausedForToolConsent) {
    return {
      label: 'Pending',
      icon: 'i-lucide-shield-check',
      colorClass: 'text-n-amber-11',
      badgeClass: 'text-n-amber-11 bg-n-amber-3',
    };
  }

  if (
    [
      'finished',
      'complete',
      'completed',
      'done',
      'success',
      'succeeded',
    ].includes(status)
  ) {
    return {
      label: 'Completed',
      icon: 'i-lucide-check-circle-2',
      colorClass: 'text-n-green-11',
      badgeClass: 'text-n-green-11 bg-n-green-3',
    };
  }

  if (['error', 'failed', 'failure', 'cancelled', 'aborted'].includes(status)) {
    return {
      label: 'Failed',
      icon: 'i-lucide-x-circle',
      colorClass: 'text-n-ruby-11',
      badgeClass: 'text-n-ruby-11 bg-n-ruby-3',
    };
  }

  if (['pending', 'queued'].includes(status)) {
    return {
      label: 'Pending',
      icon: 'i-lucide-clock-3',
      colorClass: 'text-n-amber-11',
      badgeClass: 'text-n-amber-11 bg-n-amber-3',
    };
  }

  return {
    label: 'Running',
    icon: 'i-lucide-loader-circle',
    colorClass: 'text-n-slate-10',
    badgeClass: 'text-n-slate-11 bg-n-solid-3',
  };
}

function getToolGroupSummary(group) {
  const stepCount = group?.calls?.length || 0;
  const stepWord = stepCount === 1 ? 'step' : 'steps';
  const status = group?.status;

  if (
    [
      'finished',
      'complete',
      'completed',
      'done',
      'success',
      'succeeded',
    ].includes(status)
  ) {
    return `Completed ${stepCount} ${stepWord}`;
  }

  if (['error', 'failed', 'failure', 'cancelled', 'aborted'].includes(status)) {
    return `Failed after ${stepCount} ${stepWord}`;
  }

  if (['pending', 'queued'].includes(status)) {
    return `Pending ${stepCount} ${stepWord}`;
  }

  return `Running ${stepCount} ${stepWord}`;
}

const isPendingShell = computed(() => props.message._copilotPending === true);

const showHumanBubble = computed(
  () =>
    hasNonEmptyText(rawContent.value) ||
    attachedMentions.value.length > 0 ||
    imageBlocks.value.length > 0 ||
    fileBlocks.value.length > 0 ||
    extractedFiles.value.length > 0
);

const shouldRender = computed(() => {
  if (isSystem.value) return false;
  if (isTool.value) return false;
  if (
    props.suppressReasoningOnlyBubble &&
    isAI.value &&
    !hasAnswerContent.value &&
    !(props.isLast && props.isStreaming)
  ) {
    return false;
  }
  if (isPendingShell.value) return true;
  // Keep the last AI row mounted during streaming so the feather anchor
  // stays on the message row instead of jumping from the loading row below.
  if (isAI.value && !hasAiBodyContent.value) {
    return props.isLast && props.isStreaming;
  }
  return true;
});

const showStreamingPlaceholder = computed(
  () =>
    isAI.value &&
    props.isLast &&
    (isPendingShell.value || props.isStreaming) &&
    !props.isPausedForToolConsent &&
    !hasNonEmptyText(displayResponseMarkdown.value)
);

const showAnswerLine = computed(
  () =>
    isAI.value &&
    props.isLast &&
    (showStreamingPlaceholder.value ||
      hasNonEmptyText(displayResponseMarkdown.value))
);

// Reserve feedback row height from the first streaming frame so it does not
// mount at stream-end and push the message (and feather) upward.
const showFeedbackBar = computed(() => {
  if (!isAI.value || !props.isLast) return false;
  if (props.isStreaming || isPendingShell.value) return true;
  return hasAnswerContent.value;
});

const copied = ref(false);
const feedbackRating = ref(null); // 'positive' | 'negative' | null

// Extract suggestions from parsed segments (emitted to parent for display)
const suggestions = computed(() => {
  const segs = parsedSegments.value || [];
  const suggestionSeg = segs.find(s => s.type === 'suggestions');
  if (!suggestionSeg || !suggestionSeg.content) return [];
  // Parse bullet list: lines starting with - or *
  return suggestionSeg.content
    .split('\n')
    .map(line => line.replace(/^[-*•]\s*/, '').trim())
    .filter(line => line.length > 0)
    .map(line => (line.length > 50 ? line.slice(0, 50) + '…' : line));
});

// Expose suggestions and row root for parent scroll anchoring
defineExpose({ suggestions, messageRowRef });

function parseRate(content) {
  if (!content) return null;
  try {
    const data = JSON.parse(content);
    if (!data.name) return null;
    return {
      name: data.name,
      category: data.category || 'other',
      defaultPrice: data.default_price ?? data.defaultPrice ?? null,
      unit: data.unit || '',
      currency: data.currency || 'EUR',
    };
  } catch {
    return null;
  }
}

function handleFeedback(rating) {
  feedbackRating.value = feedbackRating.value === rating ? null : rating;
  // TODO: POST feedback to backend when endpoint exists
}

function copyAnswer() {
  // Copy only response/action/text segments, excluding reasoning/thinking
  const segments = parsedSegments.value || [];
  const text =
    segments
      .filter(s => s.type !== 'thinking')
      .map(s => s.content || '')
      .join('\n')
      .trim() ||
    rawContent.value ||
    '';
  navigator.clipboard.writeText(text).then(() => {
    copied.value = true;
    setTimeout(() => {
      copied.value = false;
    }, 2000);
  });
}

function retry() {
  emit('retry');
}
</script>

<template>
  <div
    v-if="shouldRender"
    ref="messageRowRef"
    class="relative w-full group/msg"
    :class="[
      isHuman ? 'flex justify-end' : '',
      tightToPrevious ? 'pt-1 pb-2' : isFirst ? 'py-2' : 'mt-6 py-2',
    ]"
  >
    <div
      class="min-w-0"
      :class="[
        isHuman
          ? 'max-w-[85%] flex flex-col items-end gap-2'
          : 'w-full text-n-slate-12',
      ]"
    >
      <template v-if="isAI">
        <CopilotReasoning
          v-if="showReasoning"
          :content="displayReasoningContent"
          :is-streaming="isThinkingInProgress"
          :render="render"
          @content-click="handleContentClick"
        />

        <!-- Tool calls in progress -->
        <div
          v-if="hasToolCalls"
          class="space-y-1"
          :class="{ 'mb-2': hasNonEmptyText(displayResponseMarkdown) }"
        >
          <div v-for="group in groupedMessageToolCalls" :key="group.key">
            <div
              v-if="group.calls.length === 1"
              class="relative flex items-center text-xs text-n-slate-10"
            >
              <div
                class="absolute right-full mr-2 flex items-center pointer-events-none"
              >
                <div
                  class="size-3.5 shrink-0"
                  :class="[
                    getToolStatusMeta(group.status).icon,
                    getToolStatusMeta(group.status).colorClass,
                    group.status === 'running' &&
                    !toolConsentPhase &&
                    !isPausedForToolConsent
                      ? 'animate-spin'
                      : '',
                  ]"
                />
              </div>
              <CopilotRotatingText
                class-name="min-w-0 font-medium"
                :text="getToolDisplayName(group.name, group.status)"
              />
            </div>
            <button
              v-else
              class="relative w-full flex items-center gap-1.5 p-0 text-left text-xs text-n-slate-10 hover:text-n-slate-11"
              @click="toggleToolCallGroup(group.key)"
            >
              <div
                class="absolute right-full mr-2 flex items-center pointer-events-none"
              >
                <div
                  class="size-3.5 shrink-0"
                  :class="[
                    getToolStatusMeta(group.status).icon,
                    getToolStatusMeta(group.status).colorClass,
                    group.status === 'running' &&
                    !toolConsentPhase &&
                    !isPausedForToolConsent
                      ? 'animate-spin'
                      : '',
                  ]"
                />
              </div>
              <CopilotRotatingText
                class-name="min-w-0 font-medium"
                :text="getToolGroupSummary(group)"
              />
              <span class="text-[10px] text-n-slate-9">{{
                `x${group.calls.length}`
              }}</span>
              <div
                class="i-lucide-chevron-right size-3 shrink-0 transition-transform"
                :class="{ 'rotate-90': isToolCallGroupExpanded(group.key) }"
              />
            </button>

            <div
              v-if="isToolCallGroupExpanded(group.key)"
              class="pt-1 space-y-0.5"
            >
              <div
                v-for="tc in group.calls"
                :key="tc.id"
                class="relative flex items-center text-xs text-n-slate-10"
              >
                <div
                  class="absolute right-full mr-2 flex items-center pointer-events-none"
                >
                  <div
                    class="size-3.5 shrink-0"
                    :class="[
                      getToolStatusMeta(tc.status).icon,
                      getToolStatusMeta(tc.status).colorClass,
                      tc.status === 'running' &&
                      !toolConsentPhase &&
                      !isPausedForToolConsent
                        ? 'animate-spin'
                        : '',
                    ]"
                  />
                </div>
                <CopilotRotatingText
                  class-name="min-w-0"
                  :text="getToolDisplayName(tc.name, tc.status)"
                />
              </div>
            </div>
          </div>
        </div>

        <!-- Consent / approved status — stays with tools inside this message row -->
        <div v-if="$slots.consent" class="mt-2">
          <slot name="consent" />
        </div>

        <div
          v-if="
            showAnswerLine ||
            (isAI && hasAiBodyContent && displayResponseMarkdown)
          "
          class="relative min-h-7 [overflow-anchor:auto]"
        >
          <div
            v-if="showMessageFeather"
            ref="messageFeatherAnchorRef"
            class="absolute -top-1 right-full mr-2 flex h-7 w-3.5 items-end justify-end overflow-visible pointer-events-none"
          >
            <CopilotFeatherLoader
              :variant="messageFeatherVariant"
              :animated="messageFeatherAnimated"
              :settle-on-change="false"
              compact
            />
          </div>

          <p
            v-show="showStreamingPlaceholder"
            class="text-[15px] leading-7 text-n-slate-10 min-h-7"
            role="status"
            aria-live="polite"
          >
            {{ pendingLabel || t('COPILOT.THINKING') }}
          </p>

          <div
            v-show="!showStreamingPlaceholder && displayResponseMarkdown"
            class="copilot-stream-prose"
            :class="PROSE_CLASSES"
            @click="handleContentClick"
            v-html="streamProseHtml"
          />
        </div>

        <!-- Structured tag segments — actions and rates after answer -->
        <template v-if="isAI && hasAiBodyContent && !isStreaming">
          <div class="space-y-0">
            <template
              v-for="(seg, idx) in parsedSegments"
              :key="'final-' + idx"
            >
              <div
                v-if="seg.type === 'action' && hasNonEmptyText(seg.content)"
                class="flex gap-2 p-2 rounded-md bg-n-slate-3 dark:bg-n-slate-3 mt-2"
              >
                <div
                  class="i-lucide-zap size-3.5 text-n-violet-11 shrink-0 mt-0.5"
                />
                <div
                  class="text-xs text-n-slate-11 leading-relaxed break-words prose prose-sm dark:prose-invert max-w-none [&_p:last-child]:mb-0"
                  @click="handleContentClick"
                  v-html="render(seg.content)"
                />
              </div>

              <CopilotPricingItemCard
                v-else-if="seg.type === 'rate' && parseRate(seg.content)"
                class="mt-2"
                v-bind="parseRate(seg.content)"
              />
            </template>
          </div>
        </template>

        <!-- Image attachments -->
        <div
          v-if="isAI && imageBlocks.length"
          class="flex flex-wrap gap-2 mt-2"
        >
          <img
            v-for="(img, idx) in imageBlocks"
            :key="idx"
            :src="img.url || img.data"
            class="max-w-[240px] max-h-[180px] rounded-lg object-cover ring-1 ring-n-weak"
          />
        </div>

        <!-- File attachments -->
        <div v-if="isAI && fileBlocks.length" class="flex flex-wrap gap-2 mt-2">
          <a
            v-for="(file, idx) in fileBlocks"
            :key="idx"
            :href="file.url"
            target="_blank"
            rel="noopener noreferrer"
            class="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-n-solid-3 ring-1 ring-n-weak text-xs text-n-slate-12 hover:bg-n-solid-4 transition-colors"
          >
            <span class="i-lucide-file size-3.5 text-n-slate-11" />
            <span class="max-w-32 truncate">{{
              file.url?.split('/').pop() || 'File'
            }}</span>
          </a>
        </div>

        <!-- Extracted file attachments (from reloaded thread state) -->
        <div
          v-if="isAI && extractedFiles.length"
          class="flex flex-wrap gap-2 mt-2"
        >
          <span
            v-for="(file, idx) in extractedFiles"
            :key="idx"
            class="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-n-solid-3 ring-1 ring-n-weak text-xs text-n-slate-12"
          >
            <span class="i-lucide-file size-3.5 text-n-slate-11" />
            <span class="max-w-48 truncate">{{ file.name }}</span>
          </span>
        </div>

        <!-- Action buttons — reserved during stream so stream-end does not add height -->
        <div
          v-if="isAI && showFeedbackBar"
          class="flex h-7 items-center gap-0.5 mt-3 -ml-1 transition-opacity duration-200"
          :class="[
            isStreaming || isPendingShell
              ? 'invisible pointer-events-none'
              : isLast
                ? 'opacity-70 hover:opacity-100'
                : 'opacity-0 group-hover/msg:opacity-70 group-hover/msg:hover:opacity-100',
            { '!opacity-100': copied || feedbackRating },
          ]"
        >
          <button
            class="flex items-center justify-center size-7 rounded-md text-n-slate-9 hover:text-n-slate-12 hover:bg-n-solid-3 transition-colors"
            :title="t('COPILOT.COPY_ANSWER')"
            @click="copyAnswer"
          >
            <span
              class="size-3.5"
              :class="copied ? 'i-lucide-check' : 'i-lucide-copy'"
            />
          </button>
          <button
            class="flex items-center justify-center size-7 rounded-md transition-colors"
            :class="
              feedbackRating === 'positive'
                ? 'text-n-green-11 bg-n-green-3'
                : 'text-n-slate-9 hover:text-n-slate-12 hover:bg-n-solid-3'
            "
            :title="t('COPILOT.FEEDBACK_GOOD')"
            @click="handleFeedback('positive')"
          >
            <span class="i-lucide-thumbs-up size-3.5" />
          </button>
          <button
            class="flex items-center justify-center size-7 rounded-md transition-colors"
            :class="
              feedbackRating === 'negative'
                ? 'text-n-ruby-11 bg-n-ruby-3'
                : 'text-n-slate-9 hover:text-n-slate-12 hover:bg-n-solid-3'
            "
            :title="t('COPILOT.FEEDBACK_BAD')"
            @click="handleFeedback('negative')"
          >
            <span class="i-lucide-thumbs-down size-3.5" />
          </button>
          <button
            v-if="isLast"
            class="flex items-center justify-center size-7 rounded-md text-n-slate-9 hover:text-n-slate-12 hover:bg-n-solid-3 transition-colors"
            :title="t('COPILOT.TRY_AGAIN')"
            @click="retry"
          >
            <span class="i-lucide-refresh-cw size-3.5" />
          </button>
        </div>
      </template>

      <div
        v-if="isHuman && showHumanBubble"
        class="w-full rounded-2xl px-4 py-2.5 bg-n-slate-4 text-n-slate-12 space-y-2"
      >
        <div
          v-if="attachedMentions.length || rawContent"
          class="flex flex-wrap items-baseline gap-x-2 gap-y-1 text-[15px] leading-7"
          @click="handleContentClick"
        >
          <button
            v-for="mention in attachedMentions"
            :key="`${mention.type}:${mention.id}`"
            type="button"
            class="inline max-w-full font-medium text-n-brand hover:underline underline-offset-2"
            @click.stop="handleMentionOpen(mention)"
          >
            <span
              class="mr-1 inline-block size-3.5 align-[-0.125em]"
              :class="mentionChipIcon(mention.type)"
            />
            <span class="truncate">{{ mention.name }}</span>
          </button>
          <span v-if="rawContent" class="min-w-0 whitespace-pre-wrap">{{
            rawContent
          }}</span>
        </div>

        <div v-if="imageBlocks.length" class="flex flex-wrap gap-2">
          <img
            v-for="(img, idx) in imageBlocks"
            :key="idx"
            :src="img.url || img.data"
            class="max-w-[240px] max-h-[180px] rounded-lg object-cover ring-1 ring-n-weak"
          />
        </div>

        <div v-if="fileBlocks.length" class="flex flex-wrap gap-2">
          <a
            v-for="(file, idx) in fileBlocks"
            :key="idx"
            :href="file.url"
            target="_blank"
            rel="noopener noreferrer"
            class="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-n-solid-3 ring-1 ring-n-weak text-xs text-n-slate-12 hover:bg-n-solid-4 transition-colors"
          >
            <span class="i-lucide-file size-3.5 text-n-slate-11" />
            <span class="max-w-32 truncate">{{
              file.name || file.url?.split('/').pop() || 'File'
            }}</span>
          </a>
        </div>

        <div v-if="extractedFiles.length" class="flex flex-wrap gap-2">
          <span
            v-for="(file, idx) in extractedFiles"
            :key="idx"
            class="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-n-solid-3 ring-1 ring-n-weak text-xs text-n-slate-12"
          >
            <span class="i-lucide-file size-3.5 text-n-slate-11" />
            <span class="max-w-48 truncate">{{ file.name }}</span>
          </span>
        </div>
      </div>
    </div>
  </div>
</template>
